import React from 'react';
import { useParams } from 'react-router-dom';
import { motion } from "motion/react";
import ClubHeader from '@/features/clubs/components/ClubHeader';
import ClubFooter from '@/features/clubs/components/ClubFooter';
import ClubHero from '@/features/clubs/components/ClubHero';
import FeatureSection from '@/features/clubs/components/FeatureSection';
import CTACard from '@/features/clubs/components/CTACard';
import UpcomingEventsSection from '@/features/clubs/components/UpcomingEventsSection';
import StructuredContentSection from '@/features/clubs/components/StructuredContentSection';
import ArtistsSection from '@/features/clubs/components/ArtistsSection';
import { ArrowRightIcon, MapPinIcon } from '@/features/clubs/components/icons/ClubPageIcons';
import { useClubData } from '@/features/clubs/hooks';
import { useLoadingContext } from '@/hooks';


const ClubPage: React.FC = () => {
    const { handle } = useParams<{ handle: string }>();
    const { club, events, artists, loading, error } = useClubData(handle || '');
    const { startLoading, stopLoading } = useLoadingContext();
    // Trigger loading when data is loading
    React.useEffect(() => {
        if (loading) {
            startLoading('club-data');
        } else {
            stopLoading('club-data');
        }
        
        // Cleanup on unmount
        return () => {
            stopLoading('club-data');
        };
    }, [loading, startLoading, stopLoading]);

    if (loading) {
        return (
            <div className="bg-black min-h-screen">
                {/* Loading is handled by the global loader */}
            </div>
        );
    }

    if (error || !club) {
        return (
            <div className="bg-black text-white min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold mb-4">Venue Not Found</h1>
                    <p className="text-gray-400 mb-6">
                        {error || 'The venue you are looking for does not exist.'}
                    </p>
                    <a 
                        href="/venues" 
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Back to Venues
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-black text-white">
            <ClubHeader clubName={club.name} clubHandle={handle || ''} clubId={club.id} />
            <main>
                <ClubHero 
                    clubName={club.name} 
                    media={club.media || null} 
                    heroSection={club.structured_description?.find(s => s.section_type === 'hero')}
                />
                
                {/* Render structured content sections if available */}
                {club.structured_description && club.structured_description.length > 0 ? (
                    <StructuredContentSection 
                        sections={club.structured_description.filter(s => s.section_type !== 'hero' && s.section_type !== 'gallery' && s.section_type !== 'cta')} 
                        clubData={club}
                    />
                ) : (
                    <FeatureSection
                        title={`About ${club.name}`}
                        body={club.description}
                        image={club.media?.[1]?.media_id || 'dj-view'}
                        layout="imageRight"
                    >
                        {/* Details Card for FeatureSection's children */}
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }} 
                            animate={{ opacity: 1, scale: 1 }} 
                            transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" as const }} 
                            className="bg-gray-900/50 backdrop-blur-md border border-gray-800 rounded-3xl p-8 sticky top-32"
                        >
                            <h3 className="text-2xl font-serif font-medium text-white mb-6">Details</h3>
                            <div className="flex items-start gap-4 mb-8">
                                <MapPinIcon />
                                <div>
                                    <p className="font-medium text-white">{club.location.address_line1}</p>
                                    <p className="text-gray-400">{`${club.location.city}, ${club.location.country}`}</p>
                                </div>
                            </div>
                            
                            {/* Age Requirements */}
                            {club.age_requirements && (
                                <div className="mb-6 space-y-3">
                                    <div className="w-full h-px bg-gradient-to-r from-gray-600 via-gray-700 to-transparent"></div>
                                    <div className="flex items-center gap-4 text-gray-300">
                                        <span>Entry: {club.age_requirements.minimum_entry_age}+</span>
                                        <span>•</span>
                                        <span>Drinking: {club.age_requirements.drinking_age}+</span>
                                    </div>
                                    <p className="text-sm text-gray-400">{club.age_requirements.notes}</p>
                                    <div className="w-full h-px bg-gradient-to-r from-gray-600 via-gray-700 to-transparent"></div>
                                </div>
                            )}
                            
                            {/* Tags */}
                            {club.tags && club.tags.length > 0 && (
                                <div className="mb-6 flex flex-wrap gap-1.5">
                                    {club.tags.map((tag, index) => (
                                        <span
                                            key={index}
                                            className="px-2 py-1 bg-gray-700/30 text-gray-300 text-sm rounded-sm hover:bg-gray-600/30 transition-colors"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                            
                            <a 
                                href={`/club/${handle}/book-table`} 
                                className="group w-full flex items-center justify-center px-8 py-4 bg-blue-600 text-white rounded-full font-medium transition-all duration-300 hover:bg-blue-500 transform hover:scale-102"
                            >
                                <span className="flex items-center gap-2">Book a Table <ArrowRightIcon /></span>
                            </a>
                        </motion.div>
                    </FeatureSection>
                )}
                
                {events && events.length > 0 && (
                    <UpcomingEventsSection events={events} clubHandle={handle || ''} />
                )}
                
                {/* Artists Section */}
                {artists && artists.length > 0 && (
                    <ArtistsSection artists={artists} clubHandle={handle} />
                )}
                
                {/* CTA Section */}
                {club.structured_description?.find(s => s.section_type === 'cta') ? (
                    <CTACard 
                        title={club.structured_description.find(s => s.section_type === 'cta')!.title}
                        body={club.structured_description.find(s => s.section_type === 'cta')!.body}
                        ctaText="Make a Booking"
                        ctaLink={`/club/${handle}/book-table`}
                        backgroundImage={club.structured_description.find(s => s.section_type === 'cta')!.image_id}
                    />
                ) : (
                    <CTACard 
                        title="Experience The Unforgettable"
                        body="Don't just hear about the legend, become part of it. Secure your place on the dance floor for a night that transcends the ordinary."
                        ctaText="Make a Booking"
                        ctaLink={`/club/${handle}/book-table`}
                        backgroundImage={club.media?.[2]?.media_id || 'crowd-shot'}
                    />
                )}
                
            </main>
            <ClubFooter clubData={club} />
        </div>
    );
};

export default ClubPage;
